import { useModalContentToggler } from '@components'

import { Button, ButtonsGroup, useModal } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { BankMatchUiFlow, UiSource } from '@features/reconciliation/enums'
import { useReconcileBill } from '@views/bills/hooks/useReconcileBill'

import { QueryKeys } from '../../../../enums/queryKeys'
import { useSegment } from '../../../../hooks'
import { BillType } from '../../../bills/enums/billType'
import { isSubjectMatchButtonDisabled } from '../../../bills/utils/isSubjectMatchButtonDisabled'
import { useManualPayment, useMatch, useMultiplePayableSubjects, usePaymentModal } from '../../contexts'
import { SubjectReconciliationModalStateId } from '../../enums/SubjectReconciliationModalStateId'
import { useCreateSubjectReconciliationSuggestion } from '../../hooks'
import { getDifferenceType } from '../BankLinesModalFooter/utils'
import { MultipleSubjectsButton } from '../MultipleSubjectsButton'

export const BillMatchModalFooter = (): ReactElement => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { track } = useSegment()
  const { selectedDifferenceType, modalId, isCrossCurrency } = usePaymentModal()
  const { hasMatchDifference, matchSuggestion } = useMatch()
  const { crossCurrencyPaymentAmount } = useManualPayment()

  const { setActiveState } = useModalContentToggler()
  const { isProcessing } = useCreateSubjectReconciliationSuggestion()
  const { close: closeMatchModal } = useModal(modalId)
  const { initialSubject, selectedSubjectsAmountsSum } = useMultiplePayableSubjects()

  const initialSubjectId = initialSubject?.id

  const differenceType = useMemo(
    () => getDifferenceType(selectedSubjectsAmountsSum, crossCurrencyPaymentAmount, selectedDifferenceType),
    [selectedSubjectsAmountsSum, crossCurrencyPaymentAmount, selectedDifferenceType],
  )

  const handleSuccessfulReconciliation = async () => {
    closeMatchModal()
    track('xxx - expenses - Pay and reconcile modal - register payment clicked')
    if (initialSubjectId) {
      await queryClient.cancelQueries({ queryKey: [QueryKeys.BillReconciliationMatches, initialSubjectId] })
      queryClient.setQueryData([QueryKeys.BillReconciliationMatches, initialSubjectId], undefined)
    }
    queryClient.invalidateQueries([QueryKeys.Bills])
  }

  const { reconcileSubject, isProcessing: isProcessingReconcile } = useReconcileBill({
    selectedDifferenceType,
    onSuccess: handleSuccessfulReconciliation,
    billId: initialSubjectId,
    organizationId: initialSubject?.organizationId,
    crossCurrencyPaymentAmount,
    bankLineId: matchSuggestion?.id,
    differenceType,
    isAutoMatch: true,
    uiSource: UiSource.PayAndRecon,
    uiFlow: BankMatchUiFlow.Matched,
  })

  const isEnterPaymentButtonDisabled = useMemo(() => {
    return isSubjectMatchButtonDisabled({
      isProcessing,
      isCrossCurrency,
      crossCurrencyPaymentAmount,
      hasMatchDifference,
      selectedDifferenceType,
    })
  }, [isProcessing, isCrossCurrency, crossCurrencyPaymentAmount, hasMatchDifference, selectedDifferenceType])

  const handleEnterPayment = useCallback(() => {
    reconcileSubject()
  }, [reconcileSubject])

  const handleRegisterMultipleBillsButton = useCallback(() => {
    track('xxx - expenses - Pay and reconcile modal - register mutliple bills clicked')

    setActiveState(SubjectReconciliationModalStateId.MultipleSubjects)
  }, [setActiveState, track])

  return (
    <ButtonsGroup>
      <MultipleSubjectsButton onClick={handleRegisterMultipleBillsButton} payableSubjectType={BillType.Bill} />
      <Button onClick={handleEnterPayment} loading={isProcessingReconcile} disabled={isEnterPaymentButtonDisabled}>
        {t('bill.payment_reconcile.button.enter_payment_and_reconcile')}
      </Button>
    </ButtonsGroup>
  )
}
